import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { Link } from "react-router-dom";
import { FiEdit3, FiEye, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const API = process.env.REACT_APP_API_BASE_URL;

const FeatureTable = () => {
  const [rows, setRows] = useState([]);
  const [valueModal, setValueModal] = useState({ open: false, feature: null });
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  /* ------------ DATA ------------ */
  const fetchData = async () => {
    const { data } = await axios.get(`${API}/api/features`);
    // Beklenen alanlar: id/feature_id, feature_title/title, value, status, is_required, is_multiple
    // Hem “feature+value” satırı gelse hem de sadece feature gelse çalışır:
    const map = new Map();
    data.forEach((r) => {
      const fid = r.feature_id ?? r.id;
      const ftitle = r.feature_title ?? r.title;
      if (!map.has(fid)) {
        map.set(fid, {
          id: fid,
          title: ftitle,
          is_required: !!r.is_required,
          is_multiple: !!r.is_multiple,
          status: r.feature_status || r.status || "aktif",
          values: [],
        });
      }
      // value satırı varsa ekle
      if (r.value !== undefined && r.value !== null) {
        map.get(fid).values.push({
          id: r.value_id ?? r.id, // value id backend'de farklıysa value_id kullan
          value: r.value,
          status: r.status, // "aktif"/"pasif" bekleniyor
        });
      }
    });
    setRows(Array.from(map.values()));
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ------------ MUTATIONS ------------ */
  const updateFeature = async (featureId, payload) => {
    await axios.put(`${API}/api/features/${featureId}`, payload);
    fetchData();
  };

  const toggleValueStatus = async (valueId, newStatus) => {
    await axios.put(`${API}/api/features/feature-values/${valueId}`, {
      status: newStatus,
    });
    fetchData();
  };

  const deleteValue = (valueId) => {
    confirmAlert({
           title: getText("DESTROY_CONFIRM_TITLE"),
      message: getText("DESTROY_CONFIRM_MSG"),
      buttons: [
        {
          label: getText("YES_SOFT_DELETE"),
          onClick: async () => {
            await axios.delete(`${API}/api/features/feature-values/${valueId}`);
             toast.success(getText("SUCCESS_DELETED"));
            fetchData();
          },
        },
        { label: getText("CANCEL")},
      ],
    });
  };

  const deleteFeature = (featureId) => {
    confirmAlert({
   title: getText("DESTROY_CONFIRM_TITLE"),
      message: getText("DESTROY_CONFIRM_MSG"),
      buttons: [
        {
         label: getText("YES_DELETE"),
          onClick: async () => {
            try {
              await axios.delete(`${API}/api/features/${featureId}`);
              toast.success(getText("SUCCESS_DELETED"));
              fetchData();
            } catch (err) {
               toast.error(getText("ERROR_DELETE_FAILED"));
            }
          },
        },
        { label: getText("CANCEL")},
      ],
    });
  };

  const Switch = ({ checked, onChange }) => (
    <div className="form-check form-switch text-center">
      <input
        className="form-check-input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ cursor: "pointer", width: 45, height: 22 }}
      />
    </div>
  );

  /* ------------ COLUMNS ------------ */
  const columns = useMemo(
    () => [
      { accessorKey: "id", header: getText("TABLE_ID_LABEL"), size: 70 },
      { accessorKey: "title", header: getText("TABLE_TITLE_LABEL") },

      // Zorunlu
      {
        accessorKey: "is_required",
       header: getText("TABLE_COMPULSORY_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.is_required}
            onChange={() =>
              updateFeature(row.original.id, {
                is_required: !row.original.is_required,
                is_multiple: row.original.is_multiple,
              })
            }
          />
        ),
      },

      // Çoklu
      {
        accessorKey: "is_multiple",
        header: getText("TABLE_MULTIPLE_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.is_multiple}
            onChange={() =>
              updateFeature(row.original.id, {
                is_multiple: !row.original.is_multiple,
                is_required: row.original.is_required,
              })
            }
          />
        ),
      },
      {
        accessorKey: "status",
         header: getText("TABLE_STATUS_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.status === "aktif"}
            onChange={() => {
              const newStatus =
                row.original.status === "aktif" ? "pasif" : "aktif";

              axios
                .put(`${API}/api/features/${row.original.id}/status`, {
                  status: newStatus,
                })
                .then(() => {
                  toast.success(`Durum güncellendi`);
                  fetchData();
                })
                .catch(() => toast.error("Durum güncellenemedi!"));
            }}
          />
        ),
      },

      // İşlemler
      {
        accessorKey: "actions",
         header: getText("TABLE_ACTIONS_LABEL"),
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Link
              to={`/features/view/${row.original.id}`}
              className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
               title={getText("VIEW")}
            >
              <FiEye />
            </Link>
            <Link
              to={`/features/edit/${row.original.id}`}
              className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
               title={getText("EDIT")}
            >
              <FiEdit3 />
            </Link>
            <button
              className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
               title={getText("DELETE")}
              onClick={() => deleteFeature(row.original.id)}
            >
              <FiTrash2 />
            </button>
          </div>
        ),
      },
    ],
    [translations],
  );

  return (
    <>
      <Table data={rows} columns={columns} />

      {/* Value Modal */}
      {valueModal.open && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Değerler — {valueModal.feature?.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setValueModal({ open: false, feature: null })}
                />
              </div>
              <div className="modal-body">
                {(valueModal.feature?.values || []).length === 0 && (
                  <div className="text-muted">Kayıtlı değer yok.</div>
                )}
                {(valueModal.feature?.values || []).map((v) => (
                  <div
                    key={v.id}
                    className="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
                  >
                    <div className="fw-medium">{v.value}</div>
                    <div className="d-flex align-items-center gap-3">
                      <Switch
                        checked={v.status === "aktif"}
                        onChange={() =>
                          toggleValueStatus(
                            v.id,
                            v.status === "aktif" ? "pasif" : "aktif",
                          )
                        }
                      />
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteValue(v.id)}
                      >
                        Sil
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setValueModal({ open: false, feature: null })}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => setValueModal({ open: false, feature: null })}
          />
        </div>
      )}
    </>
  );
};

export default FeatureTable;
